import { createRouter, createWebHistory } from 'vue-router';
import { signOut, doesSessionExist, getUserAsync } from './composables/useAuth.js';

import DashboardPage from './pages/DashboardPage.vue';
import LoggedUserPage from './pages/LoggedUserPage.vue';
import LoginPage from './pages/LoginPage.vue';
import ProgramsPage from './pages/ProgramsPage.vue';
import ProgramPage from './pages/ProgramPage.vue';
import ProgramSeoPage from './pages/ProgramSeoPage.vue';
import PluginsPage from './pages/PluginsPage.vue';
import PluginPage from './pages/PluginPage.vue';
import UsersPage from './pages/UsersPage.vue';
import UserPage from './pages/UserPage.vue';
import NotFoundPage from './pages/NotFoundPage.vue';
import StripeWebhookEventsPage from './pages/StripeWebhookEventsPage.vue';
import StripeOutboxEventsPage from './pages/StripeOutboxEventsPage.vue';
import SessionsPage from './pages/SessionsPage.vue';
import SessionPage from './pages/SessionPage.vue';
import ActionLogs from './pages/ActionLogs.vue';
import ActionLog from './pages/ActionLog.vue';
import BannersPage from './pages/BannersPage.vue';
import BannerPage from './pages/BannerPage.vue';
import OnboardingSlidesPage from './pages/OnboardingSlidesPage.vue';
import OnboardingSlidePage from './pages/OnboardingSlidePage.vue';
import DataCentersPage from './pages/DataCentersPage.vue';
import DataCenterPage from './pages/DataCenterPage.vue';
import ClusterPage from './pages/ClusterPage.vue';
import LoginAttempts from './pages/LoginAttempts.vue';
import LoginAttemptsByUser from './pages/LoginAttemptsByUser.vue';
import MaintenancePage from './pages/MaintenancePage.vue';
import VersionManagement from './pages/VersionManagement.vue';
import DataSyncPage from './pages/DataSyncPage.vue';
import PaymentEventLogsPage from './pages/PaymentEventLogsPage.vue';
import PaymentEventLogPage from './pages/PaymentEventLogPage.vue';

let authorizeResult = null;

async function isAuthorized() {
  if (authorizeResult === null) {
    const _isAuthorized = await doesSessionExist();
    authorizeResult = { isAuthorized: _isAuthorized };
  }

  return authorizeResult.isAuthorized;
}

function allowRoles(roles) {
  return async () => {
    const user = await getUserAsync();
    if (roles.includes(user.value.tier)) {
      return true;
    }

    return '/';
  };
}

const routes = [
  {
    path: '/',
    component: LoggedUserPage,
    beforeEnter: async () => {
      if (await isAuthorized()) {
        return true;
      }

      return '/login';
    },
    children: [
      {
        path: '',
        redirect: '/programs'
      },
      {
        path: 'dashboard',
        component: DashboardPage
      },
      {
        path: 'programs',
        component: ProgramsPage
      },
      {
        path: 'program/:programId',
        component: ProgramPage,
        props: true,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'program/seo/:programId',
        component: ProgramSeoPage,
        props: true
      },
      {
        path: 'program',
        component: ProgramPage,
        props: { programId: null }
      },
      {
        path: 'plugins',
        component: PluginsPage
      },
      {
        path: 'plugin/:pluginId',
        component: PluginPage,
        props: true,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'plugin',
        component: PluginPage,
        props: { pluginId: null }
      },
      {
        path: 'users',
        component: UsersPage,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'user/:userId',
        component: UserPage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'user',
        component: UserPage,
        props: { userId: null },
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'stripe-webhook-events',
        component: StripeWebhookEventsPage,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'stripe-outbox-events',
        component: StripeOutboxEventsPage,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'sessions',
        component: SessionsPage,
        beforeEnter: allowRoles([ 'admin', 'seo', 'support', 'session-analytics' ])
      },
      {
        path: 'session/:id',
        component: SessionPage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'seo', 'session-analytics' ])
      },
      {
        path: 'action-logs',
        component: ActionLogs,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'action-log/:id',
        component: ActionLog,
        props: true,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'banners',
        component: BannersPage
      },
      {
        path: 'banner/:id',
        component: BannerPage,
        props: true
      },
      {
        path: 'banner',
        component: BannerPage,
        props: { id: null }
      },
      {
        path: 'onboarding-slides',
        component: OnboardingSlidesPage,
        beforeEnter: allowRoles([ 'admin', 'seo' ])
      },
      {
        path: 'onboarding-slide/:id',
        component: OnboardingSlidePage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'seo' ])
      },
      {
        path: 'onboarding-slide',
        component: OnboardingSlidePage,
        props: { id: null },
        beforeEnter: allowRoles([ 'admin', 'seo' ])
      },
      {
        path: 'data-centers',
        component: DataCentersPage,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'data-center',
        component: DataCenterPage,
        props: { id: null },
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'data-center/:id',
        component: DataCenterPage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'data-center/:dataCenterId/cluster',
        component: ClusterPage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'data-center/:dataCenterId/cluster/:id',
        component: ClusterPage,
        props: true,
        beforeEnter: allowRoles([ 'admin', 'support' ])
      },
      {
        path: 'login-attempts',
        component: LoginAttempts,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'user-login-attempts/:email',
        component: LoginAttemptsByUser,
        props: true,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'maintenance',
        component: MaintenancePage,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'versions',
        component: VersionManagement,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'data-sync',
        component: DataSyncPage,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'payment-event-logs',
        component: PaymentEventLogsPage,
        beforeEnter: allowRoles([ 'admin' ])
      },
      {
        path: 'payment-event-log/:id',
        component: PaymentEventLogPage,
        props: true,
        beforeEnter: allowRoles([ 'admin' ])
      }
    ]
  },

  {
    path: '/login',
    component: LoginPage,
    beforeEnter: async () => {
      if (await isAuthorized()) {
        return '/programs';
      }

      return true;
    }
  },
  {
    path: '/logout',
    component: {
      render: () => {}
    },
    beforeEnter: async () => {
      await signOut();
      router.push('/login'); // eslint-disable-line no-use-before-define
    }
  },

  {
    path: '/404',
    name: 'NotFound',
    component: NotFoundPage
  },
  {
    path: '/:pathMatch(.*)*',
    component: NotFoundPage
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    const element = document.querySelector('#app');
    if (element) {
      element.scrollTo(0, 0);
      return null;
    }

    return { top: 0 };
  }
});

router.afterEach(() => {
  // reset the authorization check
  authorizeResult = null;
});

export default router;
